import React, { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { findUserByUsername } from "../penpal";
import Window from "./Window";
import Icon from "./Icon";
import { isReservedUsername } from "../welcome";

// For older accounts that never got a username: pick one, once.
export default function ChooseUsername({
  uid,
  onDone,
}: {
  uid: string;
  onDone: (username: string) => void;
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const handle = name.trim().replace(/^@/, "").toLowerCase();
    setError("");
    if (!/^[a-z0-9_.-]{3,24}$/.test(handle)) {
      setError("Use 3 to 24 letters, numbers, dots, dashes, or underscores.");
      return;
    }
    if (isReservedUsername(handle)) {
      setError(`@${handle} is saved for the PenPal Board team. Try another?`);
      return;
    }
    setBusy(true);
    try {
      if (await findUserByUsername(handle)) {
        setError(`@${handle} is already taken. Try another?`);
        return;
      }
      await updateDoc(doc(db, "users", uid), { username: handle });
      onDone(handle);
    } catch (err) {
      console.error(err);
      setError("Sorry, that didn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Window title="choose_username.exe" tone="pink" minimizable={false}>
      <form onSubmit={save} className="flex flex-col gap-3">
        <h2 className="window-heading">Choose your username</h2>
        <p className="text-sm">
          Your account doesn't have a username yet, so your profile shows
          "@penpal". Pick one so pen pals can find you. You can only choose it
          once.
        </p>
        <label className="field">
          <span className="field-label">Username</span>
          <input
            className="input"
            value={name}
            maxLength={25}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. moth.letters"
            autoComplete="username"
            required
          />
          <span className="text-xs text-ink-soft">
            3 to 24 lowercase letters, numbers, dots, dashes, or underscores.
          </span>
        </label>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn self-start" disabled={busy}>
          <Icon name="check" size={16} /> {busy ? "Saving..." : "Save username"}
        </button>
      </form>
    </Window>
  );
}
